import React from 'react';
import { ShoppingBag, Loader2, X, RefreshCw } from 'lucide-react';
import type { GeneratedProduct, TemplateConfig } from '../../services/api';
import type { Creation, Product } from '../../types/create';

interface CustomizedProductsProps {
  selectedCreation: Creation | undefined;
  currentTransformedImage: string | null;
  loadingProducts: boolean;
  isGeneratingProducts: boolean;
  currentProducts: GeneratedProduct[];
  selectedProduct: Product | GeneratedProduct | null;
  isDetailOpen: boolean;
  setIsDetailOpen: (isOpen: boolean) => void;
  setSelectedProduct: (product: Product | GeneratedProduct | null) => void;
  onCheckout: () => void;
  templateConfig: TemplateConfig | null;
  loadingSecondaryTemplates: string[];
  onRegenerate?: () => void;
}

export const CustomizedProducts: React.FC<CustomizedProductsProps> = ({
  selectedCreation,
  currentTransformedImage,
  loadingProducts,
  isGeneratingProducts,
  currentProducts,
  selectedProduct,
  isDetailOpen,
  setIsDetailOpen,
  setSelectedProduct,
  onCheckout,
  templateConfig,
  loadingSecondaryTemplates,
  onRegenerate
}) => {
  const displayImage = currentTransformedImage || selectedCreation?.transformedImageUrl;
  const productsToShow = (currentProducts.length > 0 || (selectedCreation?.products || []).length > 0) 
      ? (currentProducts.length > 0 ? currentProducts : selectedCreation!.products) 
      : [];

  // Helper to check type
  const isGenerated = (p: Product | GeneratedProduct): p is GeneratedProduct => 'previewUrl' in p;

  const determineProductType = (product: Product | GeneratedProduct | string): string => {
      let text = '';
      let templateId = '';

      if (typeof product === 'string') {
          text = product; // templateId
          templateId = product;
      } else if (isGenerated(product)) {
          text = product.title;
          templateId = product.templateId;
      } else {
          text = product.name; // fallback for historical products without templateId
      }

      if (templateConfig) {
          if (templateConfig.canvas.includes(templateId)) return 'Canvas';
          if (templateConfig.fineArt.includes(templateId)) return 'Fine Art';
          if (templateConfig.classicMattePaper.includes(templateId)) return 'Matte Paper';
          if (templateConfig.acrylic.includes(templateId)) return 'Acrylic Print';
      }

      const t = text.toLowerCase();
      if (t.includes("canvas")) return 'Canvas';
      if (t.includes("fine art")) return 'Fine Art';
      if (t.includes("matte paper")) return 'Matte Paper';
      if (t.includes("acrylic")) return 'Acrylic Print';
      if (t.includes("mug") || t.includes("ceramic") || t.includes("cup")) return 'Mug';
      if (t.includes("poster")) return 'Poster';
      
      return 'Other Products';
  };

  // Group products
  const groupedProducts: Record<string, (Product | GeneratedProduct | { type: 'loading', id: string })[]> = {};
  
  // 1. Add existing products
  productsToShow.forEach(p => {
      const type = determineProductType(p);
      if (!groupedProducts[type]) groupedProducts[type] = [];
      groupedProducts[type].push(p);
  });

  // 2. Add loading placeholders
  loadingSecondaryTemplates.forEach(templateId => {
      const type = determineProductType(templateId);
      if (!groupedProducts[type]) groupedProducts[type] = [];
      groupedProducts[type].push({ type: 'loading', id: templateId });
  });

  // Order of sections
  const sectionOrder = ['Canvas', 'Fine Art', 'Matte Paper', 'Acrylic Print', 'Mug', 'Poster', 'Other Products'];
  const sortedSections = sectionOrder.filter(s => groupedProducts[s] && groupedProducts[s].length > 0);
  
  // Add any remaining sections that weren't in the predefined order
  Object.keys(groupedProducts).forEach(key => {
      if (!sortedSections.includes(key)) sortedSections.push(key);
  });

  return (
    <div>
      <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
        <ShoppingBag className="w-5 h-5" />
        {selectedCreation ? 'Available Products' : 'Custom Products'}
      </h2>
      
      {displayImage && (
        <div className="mb-8 flex flex-col gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-md overflow-hidden shadow-sm flex-shrink-0">
              <img src={displayImage} alt="Transformed" className="w-full h-full object-cover" />
            </div>
            <div>
              <h3 className="font-medium">Your Masterpiece</h3>
              <p className="text-sm text-gray-500">Applied on high-quality products</p>
            </div>
          </div>
          
          {onRegenerate && (
            <button
                onClick={onRegenerate}
                disabled={isGeneratingProducts || loadingProducts}
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium
                    ${isGeneratingProducts || loadingProducts 
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    }`}
            >
                <RefreshCw className={`w-4 h-4 ${isGeneratingProducts || loadingProducts ? 'animate-spin' : ''}`} />
                {isGeneratingProducts || loadingProducts ? 'Regenerating...' : 'Re-generate Products'}
            </button>
          )}
        </div>
      )}

      {(loadingProducts || (isGeneratingProducts && productsToShow.length === 0)) ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">Generating your custom products...</p>
          <p className="text-sm text-gray-400 mt-2">This may take a few minutes</p>
        </div>
      ) : (
        <div className="space-y-12">
          {sortedSections.length > 0 ? (
             sortedSections.map(section => (
                 <div key={section}>
                     <h3 className="text-lg font-serif font-semibold text-gray-800 mb-4">{section}</h3>
                     <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {groupedProducts[section].map((item, idx) => {
                            if ('type' in item && item.type === 'loading') {
                                return (
                                    <div key={`loading-${item.id}-${idx}`} className="p-2 rounded-xl bg-gray-50 border border-gray-100 flex flex-col items-center justify-center min-h-[200px]">
                                        <Loader2 className="w-6 h-6 animate-spin text-gray-300 mb-2" />
                                        <span className="text-xs text-gray-400">Loading...</span>
                                    </div>
                                );
                            }
                            
                            const product = item as Product | GeneratedProduct;
                            const isGen = isGenerated(product);
                            const pGen = isGen ? (product as GeneratedProduct) : null;
                            const pHist = !isGen ? (product as Product) : null;

                            const imageUrl = pGen ? pGen.previewUrl : pHist!.image;
                            const productName = pGen ? pGen.title : pHist!.name;
                            
                            let productPriceStr = pHist ? pHist.price : '$ --';
                            if (pGen) {
                                const priceEntry = pGen.productPrice;
                                if (priceEntry && typeof priceEntry.price === 'number') {
                                    const currencySymbol = (priceEntry.currency === 'USD' || !priceEntry.currency) ? '$' : (priceEntry.currency + ' ');
                                    productPriceStr = `${currencySymbol}${priceEntry.price.toFixed(2)}`;
                                }
                            }

                            const isSelected = (isGen ? selectedProduct && isGenerated(selectedProduct) && (selectedProduct as GeneratedProduct).id === pGen!.id : selectedProduct && !isGenerated(selectedProduct) && (selectedProduct as Product).id === pHist!.id);

                            return (
                                <div 
                                  key={pGen ? pGen.id : pHist!.id} 
                                  className={`group cursor-pointer transition-all p-2 rounded-xl ${
                                    isSelected
                                      ? 'ring-2 ring-black bg-gray-50 shadow-sm' 
                                      : 'hover:bg-gray-50'
                                  }`}
                                  onClick={() => {
                                    setSelectedProduct(product);
                                    setIsDetailOpen(true);
                                  }}
                                >
                                  <div className="aspect-[4/5] bg-gray-100 rounded-lg overflow-hidden mb-3 relative">
                                    <img 
                                      src={imageUrl} 
                                      alt={productName} 
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className={`absolute inset-0 transition-colors ${
                                       isSelected ? 'bg-black/0' : 'bg-black/0 group-hover:bg-black/10'
                                    }`} />
                                  </div>
                                  <h3 className="font-medium text-gray-900 truncate">{productName}</h3>
                                  <p className="text-sm text-gray-500">{productPriceStr}</p>
                                </div>
                            );
                        })}
                     </div>
                 </div>
             ))
          ) : (
            <div className="col-span-full text-center py-12 text-gray-500 flex flex-col items-center">
              <p className="mb-4">
              {selectedCreation 
                ? "No products generated for this creation yet." 
                : currentTransformedImage 
                  ? "Products are being generated..."
                  : "Select a creation to view products."}
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Product Detail Modal */}
      {selectedProduct && isDetailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
                onClick={() => setIsDetailOpen(false)}
            />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row animate-in fade-in zoom-in-95 duration-200">
                <button 
                    onClick={() => setIsDetailOpen(false)}
                    className="absolute top-4 right-4 z-10 p-2 bg-white/80 hover:bg-white rounded-full text-gray-500 hover:text-gray-900 transition-colors shadow-sm backdrop-blur-md"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Image Section */}
                <div className="w-full md:w-1/2 bg-gray-50 p-6 md:p-12 flex items-center justify-center relative">
                    <img 
                        src={isGenerated(selectedProduct) ? (selectedProduct as GeneratedProduct).previewUrl : (selectedProduct as Product).image} 
                        alt={isGenerated(selectedProduct) ? (selectedProduct as GeneratedProduct).title : ((selectedProduct as Product).name)}
                        className="max-w-full max-h-[60vh] object-contain shadow-lg rounded-lg"
                    />
                </div>

                {/* Details Section */}
                <div className="w-full md:w-1/2 p-6 md:p-8 flex flex-col overflow-y-auto">
                    <h2 className="text-2xl md:text-3xl font-serif font-bold text-gray-900 mb-2">
                        {isGenerated(selectedProduct) 
                            ? (selectedProduct as GeneratedProduct).title 
                            : ((selectedProduct as Product).gelatoProduct?.title || (selectedProduct as Product).name)}
                    </h2>
                    
                    <div className="text-xl md:text-2xl text-gray-900 font-medium mb-6">
                        {(() => {
                            if (isGenerated(selectedProduct)) {
                                const priceEntry = (selectedProduct as GeneratedProduct).productPrice;
                                if (priceEntry && typeof priceEntry.price === 'number') {
                                    const currencySymbol = (priceEntry.currency === 'USD' || !priceEntry.currency) ? '$' : (priceEntry.currency + ' ');
                                    return `${currencySymbol}${priceEntry.price.toFixed(2)}`;
                                }
                                return '$ --';
                            } else {
                                return (selectedProduct as Product).price;
                            }
                        })()}
                    </div>

                    <div className="prose prose-sm text-gray-600 mb-8 flex-1 overflow-y-auto max-h-[300px]">
                        <p>
                            {isGenerated(selectedProduct) 
                                ? (selectedProduct as GeneratedProduct).description 
                                : ((selectedProduct as Product).gelatoProduct?.description || (selectedProduct as Product).type)}
                        </p>
                    </div>

                    <div className="mt-auto pt-6 border-t border-gray-100">
                        <button 
                            onClick={onCheckout}
                            className="w-full bg-black text-white px-8 py-4 rounded-xl hover:bg-gray-800 transition-all transform active:scale-[0.98] flex items-center justify-center gap-2 font-medium text-lg shadow-lg shadow-black/10"
                        >
                            <ShoppingBag className="w-5 h-5" />
                            Order Now
                        </button>
                        <p className="text-xs text-gray-400 text-center mt-3">
                            Secure checkout via Stripe
                        </p>
                    </div>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};
